import React, { useState } from 'react';
import { Button, Container, Grid, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import useTestRunner from './useTestRunner/useTestRunner';
import EdgeResult from './EdgeResult/EdgeResult';
import Alert from '../../common/Alert/Alert';
import { useAppStateContext } from '../../AppStateProvider/AppStateProvider';
import { DEFAULT_EDGE, DEFAULT_CODEC_PREFERENCES } from '../../../constants';

// Layout follows the same header/content/actions convention used by the other panes
// (see AudioTest, SupportedBrowser): a floated header column, a floated results column,
// and a cleared row of action buttons underneath.
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mainContainer: {
      display: 'block',
    },
    header: {
      float: 'left',
      [theme.breakpoints.down('md')]: {
        float: 'initial',
      },
    },
    resultsContainer: {
      width: '100%',
      float: 'right',
      marginRight: '1em',
      [theme.breakpoints.down('md')]: {
        float: 'initial',
        margin: '0 0 2em 0',
      },
    },
    actionButtons: {
      clear: 'left',
      [theme.breakpoints.down('md')]: {
        clear: 'initial',
        marginBottom: '2em',
      },
    },
  })
);

export function NetworkTest() {
  const classes = useStyles();
  const { dispatch } = useAppStateContext();
  const { isRunning, result, activeEdge, runTests, activeTest } = useTestRunner();
  const [isCompletedSuccess, setIsCompletedSuccess] = useState(false);

  async function startTest() {
    // No user-configurable settings: the preset edge(s)/codec in src/constants.ts are
    // sufficient for our users, so the Settings dialog was intentionally removed rather
    // than kept for a "just in case" future need (see IREP-12961 stub tests).
    dispatch({ type: 'preflight-started' });
    const testResult = await runTests(DEFAULT_EDGE, DEFAULT_CODEC_PREFERENCES);
    // Store the raw roaming TestResult in AppStateProvider so other panes (Results/PDF)
    // can read it later.
    dispatch({ type: 'set-network-test-result', result: testResult });
    dispatch({ type: 'preflight-finished' });
    dispatch({ type: 'next-pane' });
    setIsCompletedSuccess(testResult.results.preflight !== undefined);
  }

  const isExpired = result?.errors.bitrate?.message === 'expired' || result?.errors.preflight?.message === 'expired';

  return (
    <Container>
      <div className={classes.mainContainer}>
        <Grid item lg={5} className={classes.header}>
          <Typography variant="h1" gutterBottom>
            Check Your Network Connection
          </Typography>
          <Typography variant="body1" gutterBottom>
            Run a network test to check your connection to our servers.
          </Typography>
        </Grid>

        <Grid item lg={5} className={classes.resultsContainer}>
          {isExpired && (
            <Alert variant="error">
              <Typography variant="body1">
                <strong>App has expired</strong>&nbsp;Please redeploy the app and try again.
              </Typography>
            </Alert>
          )}
          <EdgeResult
            codecPreferences={DEFAULT_CODEC_PREFERENCES}
            edge={DEFAULT_EDGE}
            isActive={activeEdge === DEFAULT_EDGE}
            result={result ?? undefined}
            activeTest={activeTest}
          />
        </Grid>

        <Grid item lg={5} className={classes.actionButtons}>
          {isCompletedSuccess ? (
            <Button variant="contained" color="primary" onClick={() => dispatch({ type: 'next-pane' })}>
              Next
            </Button>
          ) : (
            <Button onClick={startTest} variant="contained" color="primary" disabled={isRunning}>
              Start Test
            </Button>
          )}
        </Grid>
      </div>
    </Container>
  );
}
